import React, { useEffect, lazy, Suspense } from 'react';
import { Route, Switch, useHistory } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { useLoadScript } from '@react-google-maps/api';
import { ToastContainer } from 'react-toastify';
import Nav from './Header/NavBar/NavBar';
import Loading from './Loading/Loading';
import PrivateRoute from './PrivateRoute';
import PublicRoute from './PublicRoute';
import AdminRoute from './Admin/AdminRoute';
import { useAuth } from '../hooks/useAuth';
import 'react-toastify/dist/ReactToastify.css';
import './common/BoatCard.css';
import './CaptainProfileEdit/Bookings/BoatCard.css';
import CartPage from './Cart/CartPage';
import ProductCheckout from './Cart/ProductCheckout';
import VerifyEmail from './Auth/VerifyEmail';
import PublicProfile from './PublicProfile/PublicProfile';

const Homepage = lazy(() => import('./Landing-page/LandingPage'));
const SearchResults = lazy(() => import('./boatResult/boatResult'));
const ListAboatSuccessful = lazy(() => import('./AddABoat/AddaboatSuccessful/AddaboatSuccessful'));
const Login = lazy(() => import('./Login/Login'));
const SignUp = lazy(() => import('./SignUp/SignUp'));
const SelectBoat = lazy(() => import('./SelectBoat/SelectBoat'));
const PaymentCheckOut = lazy(() => import('./PaymentCheckout/PaymentCheckOut'));
const PaymentConfirmation = lazy(() => import('./PaymentCheckout/PaymentConfirmation/PaymentConfirmation'));
const ContactUs = lazy(() => import('./ContactUs/ContactUs'));
const Help = lazy(() => import('./Help/Help'));
const AboutUs = lazy(() => import('./AboutUs/AboutUs'));
const CaptainProfile = lazy(() => import('./CaptainProfile/CaptainProfile'));
const Message = lazy(() => import('./Message/Message'));
const Conversation = lazy(() => import('./Message/Conversation'));
const Boats = lazy(() => import('./CaptainProfileEdit/Boats/Boats'));
const BoatBookingsList = lazy(() => import('./CaptainProfileEdit/Boats/Bookings/BoatBookingsList'));
const EditBoat = lazy(() => import('./CaptainProfileEdit/Boats/EditBoat/EditBoat'));
const CaptainProfileEdit = lazy(() => import('./CaptainProfileEdit/CaptainProfileEdit'));
const Settings = lazy(() => import('./CaptainProfileEdit/Settings/Settings'));
const Earnings = lazy(() => import('./CaptainProfileEdit/Earnings/Earnings'));
const TermsOfService = lazy(() => import('./Legal/TermsOfService'));
const AdminProducts = lazy(() => import('./Admin/Products/AdminProducts'));
const ProductsPage = lazy(() => import('./ProductsPage/ProductsPage'));
const MyProducts = lazy(() => import('./ProductsPage/MyProducts'));
const MySales = lazy(() => import('./ProductsPage/MySales'));
const AddProduct = lazy(() => import('./ProductsPage/AddProduct'));
const EditProduct = lazy(() => import('./ProductsPage/EditProduct'));
const FulfillFeaturedPurchase = lazy(() => import('./FulfillPurchase/FulfillFeaturedPurchase'));
const MyFavoritesPage = lazy(() => import('./MyFavoritesPage/MyFavoritesPage'));
const PurchaseReceiptPage = lazy(() => import('./MyPurchasesPage/PurchaseReceiptPage'));
const MyPurchasesPage = lazy(() => import('./MyPurchasesPage/MyPurchasesPage'));
const BookingHistory = lazy(() => import('./Admin/BookingHistory'));
const PaymentHistory = lazy(() => import('./Admin/PaymentHistory'));
const PrivacyPolicy = lazy(() => import('./Legal/PrivacyPolicy'));
const AdminDashboard = lazy(() => import('./Admin/AdminDashboard'));
const PayPalReturn = lazy(() => import('./CaptainProfileEdit/Payouts/PayPalReturn'));
const SellerStorePage = lazy(() => import('./ProductsPage/SellerStorePage'));
const CheckoutPage = lazy(() => import('./PaymentCheckout/PaymentCheckOut'));
const ListAboat = lazy(() => import('./AddABoat/Addaboat'));
const FollowingPage = lazy(() => import('./Social/FollowingPage'));
const FollowersPage = lazy(() => import('./Social/FollowersPage'));

const libraries = ["places", "geometry"];

const App = () => {
  useAuth();

  const isAuthReady = useSelector((state) => state.auth.isAuthReady);

  const { isLoaded: isMapScriptLoaded, loadError: mapScriptLoadError } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_API_KEY,
    libraries,
  });

  if (!isAuthReady) {
    return <Loading />;
  }

  return (
      <div className="App">
        <Nav />
        <Suspense fallback={<Loading />}>
          <Switch>
            <Route path="/" exact component={Homepage} />
            <Route path="/boatresults" exact><SearchResults isMapScriptLoaded={isMapScriptLoaded} mapScriptLoadError={mapScriptLoadError} /></Route>
            <PublicRoute path="/login" exact component={Login} />
            <Route path="/selectboat/:ownerUID/:boatId" exact component={SelectBoat} />
            <PublicRoute path="/signup" exact component={SignUp} />
            <Route path="/contactus" exact component={ContactUs} />
            <Route path="/help" exact component={Help} />
            <Route path="/aboutus" exact component={AboutUs} />
            <Route path="/terms" exact component={TermsOfService} />
            <Route path="/products" exact component={ProductsPage} />
            <Route path="/privacy" exact component={PrivacyPolicy} />
            <Route path="/store/:username" component={SellerStorePage} />
            <Route path="/profile/public/:userId" component={PublicProfile} />
            <Route path="/receipt/:purchaseId" exact component={PurchaseReceiptPage} />
            <Route path="/cart" component={CartPage} />
            <Route path="/verify-email" component={VerifyEmail} />
            <PrivateRoute path="/paypal/return" component={PayPalReturn} />
            <PrivateRoute path="/listaboat" exact><ListAboat isMapScriptLoaded={isMapScriptLoaded} mapScriptLoadError={mapScriptLoadError} /></PrivateRoute>
            <PrivateRoute path="/listaboat/successful" exact component={ListAboatSuccessful} />
            <PrivateRoute path="/selectboat/checkout" exact component={CheckoutPage} />
            <PrivateRoute path="/payment-confirmation/:bookingId" exact component={PaymentConfirmation} />
            <PrivateRoute path="/captain-profile" exact component={CaptainProfile} />
            <PrivateRoute path="/profile/edit" exact component={CaptainProfileEdit} />
            <PrivateRoute path="/settings" exact component={Settings} />
            <PrivateRoute path="/earnings" exact component={Earnings} />
            <PrivateRoute path="/message" exact component={Message} />
            <PrivateRoute path="/message/conversation" exact component={Conversation} />
            <PrivateRoute path="/my-reservations" exact component={BoatBookingsList} />
            <PrivateRoute path="/following" exact component={FollowingPage} />
            <PrivateRoute path="/followers" exact component={FollowersPage} />
            <PrivateRoute path="/boats" exact component={Boats} />
            <PrivateRoute path="/boats/edit/:boatId" exact><EditBoat isMapScriptLoaded={isMapScriptLoaded} mapScriptLoadError={mapScriptLoadError} /></PrivateRoute>
            <PrivateRoute path="/my-purchases" exact component={MyPurchasesPage} />
            <PrivateRoute path="/my-favorites" exact component={MyFavoritesPage} />
            <PrivateRoute path="/my-products" exact component={MyProducts} />
            <PrivateRoute path="/my-sales" exact component={MySales} />
            <PrivateRoute path="/add-product" exact component={AddProduct} />
            <PrivateRoute path="/edit-product/:productId" exact component={EditProduct} />
            <PrivateRoute path="/fulfill-purchase/:purchaseId" exact component={FulfillFeaturedPurchase} />
            <PrivateRoute path="/product-checkout" component={ProductCheckout} />
            <AdminRoute path="/admin/boats/edit/:ownerUID/:boatId" exact><EditBoat isMapScriptLoaded={isMapScriptLoaded} mapScriptLoadError={mapScriptLoadError} /></AdminRoute>
            <AdminRoute path="/admin/users/:userId/bookings" component={BookingHistory} />
            <AdminRoute path="/admin/users/:userId/payments" component={PaymentHistory} />
            <AdminRoute path="/admin" component={AdminDashboard} />
          </Switch>
        </Suspense>
        <ToastContainer
          position='top-right'
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme="colored"
        />
      </div>
  );
};

export default App;
